import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import type { PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import clsx from 'clsx';
import { startOfDay } from 'date-fns';

import ViewField from '../../../../components/reusable/view-field/ViewField';
import { getTimeValueFromOptionValue } from '../../../../utils/datetime/date-time-utils';
import { formatTrainingCalories } from '../../../../utils/training';
import { useStyles } from './StatisticsCalendar.styles';
import { useStatisticsCalendar } from './useStatisticsCalendar';

const StatisticsCalendar = () => {
  const theme = useTheme();

  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const {
    finishedTrainingDates,
    startedTrainingDates,
    planedTrainingDates,
    handleDateChange,
    selectedMonth,
    handleMonthChange,
    monthStatistics,
    isLoading,
    refetch,
  } = useStatisticsCalendar();

  const CustomDay = (props: PickersDayProps) => {
    const dayStart = startOfDay(props.day).getTime();
    const hasFinishedTraining = finishedTrainingDates.includes(dayStart);
    const hasStartedTraining = startedTrainingDates.includes(dayStart);
    const isPlanedTraining = planedTrainingDates.includes(dayStart);

    return (
      <PickersDay
        {...props}
        className={clsx(
          props.className,
          classes.day,
          hasStartedTraining && classes.startedTrainingDay,
          hasFinishedTraining && classes.trainingDay,
          isPlanedTraining && classes.planedTrainingDay,
        )}>
        {props.day.getDate()}
      </PickersDay>
    );
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.calendarWrapper}>
        <StaticDatePicker
          value={selectedMonth}
          onChange={handleDateChange}
          onMonthChange={handleMonthChange}
          slots={{
            actionBar: () => null,
            day: CustomDay,
          }}
        />
      </Box>
      <Box className={classes.selectedMonthWrapper}>
        <ViewField
          label={t('statistics.trainingsCount')}
          value={monthStatistics.count}
          loading={isLoading}
        />
        <ViewField
          className={classes.viewField}
          label={t('statistics.commonLoad')}
          value={monthStatistics.totalLoad.toFixed(2)}
          loading={isLoading}
        />
        <ViewField
          className={classes.viewField}
          label={t('statistics.averageLoad')}
          value={monthStatistics.averageLoad.toFixed(2)}
          loading={isLoading}
        />
        <ViewField
          className={classes.viewField}
          label={t('statistics.commonCalories')}
          value={formatTrainingCalories(monthStatistics.totalCalories)}
          loading={isLoading}
        />
        <ViewField
          className={classes.viewField}
          label={t('statistics.averageCalories')}
          value={formatTrainingCalories(monthStatistics.averageCalories)}
          loading={isLoading}
        />
        <ViewField
          className={classes.viewField}
          label={t('statistics.totalTime')}
          value={
            monthStatistics.totalTime > 0
              ? getTimeValueFromOptionValue(monthStatistics.totalTime, t)
              : '0s'
          }
          loading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default StatisticsCalendar;
